//  External libs
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

// Assets
import "./popupPage.css";

// Components
import { fetchFirmwareList, sendFirmwareUpdate } from "../../services/firmwareService.js";

/**
 * DeviceUpdateFirmwarePopupForm - A popup form component for updating device firmware.
 *
 * @param {Function} onClose - Callback function to close the popup.
 * @param {Function} onSubmit - Callback function executed when the form is submitted.
 * @param {Array} devices - List of devices selected for firmware update.
 */
const DeviceUpdateFirmwarePopupForm = ({
  onClose,
  onSubmit,
  devices,
}) => {
  const [firmware, setFirmware] = useState("");
  const [firmwareList, setFirmwareList] = useState([{}]);

  /**
   * Fetches the list of available firmware versions based on the device type.
   * Updates the state with the fetched firmware list.
   */
  useEffect(() => {
    const fetchFirmwares = async () => {
      if (!devices[0]?.deviceType) {
        return;
      }
      const fwList = await fetchFirmwareList(devices[0].deviceType);
      setFirmwareList(fwList);
    };

    fetchFirmwares();
    setFirmware(devices[0]?.firmware);
  }, [devices]);

  /**
   * Handles changes in the firmware selection dropdown.
   * @param {Event} e - The change event from the dropdown.
   */
  const handleChange = (e) => {
    setFirmware(e.target.value);
  };

  /**
   * Updates the firmware for all selected devices.
   * Ensures firmware selection and device ID validity before making requests.
   */
  const updateFirmware = async () => {
    if (!firmware) {
      console.error("Firmware not defined or empty string");
      return;
    }

    devices.forEach((device) => {
      if (!device?.id) {
        console.error("Device ID not defined or empty string");
        return;
      }
      if (device.firmware === firmware) {
        console.log("Firmwares already match, not updating");
        return;
      }
      sendFirmwareUpdate(device.id, firmware);
    });
  };

  /**
   * Handles the form submission, triggering the firmware update process.
   * @param {Event} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    updateFirmware();
    onSubmit(); // Pass validated form data to parent
    onClose(); // Close popup
  };

  function isFirmwareMissing() {
    return firmwareList === null || firmwareList.length === 0;
  }

  // Render the popup if it is open
  return (
    <div className="popup-overlay">
      <div className="popup">
        <div class="popup-header">
          <span class="popup-title">Update Firmware</span>
          <button class="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Device Type Dropdown */}
          <label>
            Firmware Version:
            {isFirmwareMissing() ? 
            (
              <select disabled>
                <option>No firmware found</option>
              </select>
            ) : 
            (
              <select name="firmware" value={firmware} onChange={handleChange}>
                {firmwareList.map((type, index) => (
                  <option key={index} value={type.fw_ver}>
                    {type.fw_ver}
                  </option>
                ))}
              </select>
            )}
          </label>

          {/* Buttons */}
          <button class="popup-submit-btn" type="submit" disabled={isFirmwareMissing() ? true : false}>
            Submit
          </button>
        </form>
      </div>
    </div>
  );
};

DeviceUpdateFirmwarePopupForm.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  devices: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default DeviceUpdateFirmwarePopupForm;
